import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  OnDestroy,
  inject,
  signal,
  Signal,
  SimpleChanges,
  effect
} from '@angular/core';
import { utc } from 'moment';
import { ButtonModule } from 'primeng/button';
import { Subject, takeUntil } from 'rxjs';
import { IOHAppService } from 'src/app/services/ioh-app.service';
import { IohEquipmentInfoTableComponent } from '../ioh-equipment-info-table/ioh-equipment-info-table.component';
import { BadgeModule } from 'primeng/badge';
import { CommonModule } from '@angular/common';
import { positionTerminalPopup } from 'src/app/shared/utils/terminalPopupPosition';

@Component({
  selector: 'ioh-csx-terminal-equipment',
  templateUrl: './ioh-csx-terminal-equipment.component.html',
  styleUrls: ['./ioh-csx-terminal-equipment.component.scss'],
  imports: [ButtonModule, IohEquipmentInfoTableComponent, BadgeModule,CommonModule]
})
export class IohTerminalEquipmentComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  private _terminalName = signal<string>('');
  private _terminalAbbreviation = signal<string>('');
  private _top = signal<number>(0);
  private _left = signal<number>(0);
  private _close = signal<boolean>(false);
  private _showClose = signal<boolean>(false);
  private _collapseAll = signal<boolean>(false);
  //private _level = signal<number>(0);
  private _showFilter = signal<boolean>(false);
  private _equipmentResponse = signal<any>(null);
  zIndex = 1;
  utc = utc;
  private _terminalUid = signal<string>(null);
  private _dynamicFontSize = signal<string>('');
  private destroy$ = new Subject<void>();
  appService = inject(IOHAppService);
  equipmentInfoTableColumns = [];

  rawEquipmentInfoResponse: any;
  displayEquipmentInfoTableData: any;
  totalCounts: {
    totalActiveCount: number;
    totalIdleCount: number;
    totalOfflineCount: number;
  };
  lastUpdatedEQuipmentTableData = [];
  @Input() level: number = 0;
  
  @Input() clickPoint: { x: number; y: number } = null;
  @Input() bounds: { left: number; right: number; top: number; bottom: number } = null;

  constructor() {
    this.equipmentInfoTableColumns = [
      { field: 'vehicleType', header: 'equipment', width: '55%' },
      { field: 'activeCount', header: 'active', color: '#80B682' },
      { field: 'idleCount', header: 'idle', color: '#FFBD33' },
      { field: 'offlineCount', header: 'offline', color: '#C7524E' },
      { field: 'total', header: 'total', hide: true }
    ];

    // Effect for font size
    effect(() => {
      this._dynamicFontSize.set(this.appService.iohFontSizeValue$());
    });
  }

  @Input()
  set terminalName(value: string) {
    this._terminalName.set(value);
  }
  get terminalName(): string {
    return this._terminalName();
  }

  @Input()
  set equipmentResponse(value: any) {
    this._equipmentResponse.set(value);
  }
  get equipmentResponse(): any {
    return this._equipmentResponse();
  }

  @Input()
  set terminalAbbreviation(value: string) {
    this._terminalAbbreviation.set(value);
  }
  get terminalAbbreviation(): string {
    return this._terminalAbbreviation();
  }

  @Input()
  set terminalUid(value: any) {
    this._terminalUid.set(value);
  }
  get terminalUid(): any {
    return this._terminalUid();
  }

  @Input()
  set top(value: number) {
    this._top.set(value);
  }
  get top(): number {
    return this._top();
  }

  @Input()
  set left(value: number) {
    this._left.set(value);
  }
  get left(): number {
    return this._left();
  }

  @Input()
  set close(value: boolean) {
    this._close.set(value);
  }
  get close(): boolean {
    return this._close();
  }

  @Input()
  set showClose(value: boolean) {
    this._showClose.set(value);
  }
  get showClose(): boolean {
    return this._showClose();
  }

  @Input()
  set collapseAll(value: boolean) {
    this._collapseAll.set(value);
  }
  get collapseAll(): boolean {
    return this._collapseAll();
  }

  // @Input()
  // set level(value: number) {
  //   this._level.set(value);
  // }
  // get level(): number {
  //   return this._level();
  // }

  @Input()
  set showFilter(value: boolean) {
    this._showFilter.set(value);
  }
  get showFilter(): boolean {
    return this._showFilter();
  }

  @Input()
  set dynamicFontSize(value: string) {
    this._dynamicFontSize.set(value);
  }
  get dynamicFontSize(): string {
    return this._dynamicFontSize();
  }

  @Output() closeChange = new EventEmitter<boolean>();
  @Output() moreEvent = new EventEmitter<any | undefined>();

  ngOnInit() {
    if (this.showClose) {
      this.appService.iohTableZIndexLevels[this.level] += 1;
    }
    this.zIndex = this.appService.iohTableZIndexLevels[this.level];

    this.getEquipmentTableInfo(this._terminalName());
  }

  ngAfterViewInit(): void {
    const element = document.getElementById(this.terminalUid);
    if (element) {
      this.resetElementPosition(element);
      setTimeout(() => this.resetElementPosition(element));
    }
  }

  //to get the equipement info for terminal
  getEquipmentTableInfo(terminalname) {
    let rawTableResponse = this._equipmentResponse()?.find(item =>
      terminalname.toUpperCase().includes(item.terminalName)
    );
    if (rawTableResponse) {
      this.processEquipmentTableData(rawTableResponse?.assetDetails);
    } else {
      this.displayEquipmentInfoTableData = [
        ...this.lastUpdatedEQuipmentTableData
      ];
    }
  }

  //to map vehicle type to data
  processEquipmentTableData(rawTableResponse: any) {
    const vehicleTypeMap: { [key: string]: string } = {
      UTR: 'UTR',
      CHE: 'Lift',
      GRUNT: 'Grunt',
      WSC_CLIENT: 'WSC',
      'YARD CHECK': 'Pickup Truck'
    };

    this.displayEquipmentInfoTableData = rawTableResponse
      .map(item => {
        const totalCount =
          item.activeCount + item.idleCount + item.offlineCount;
        const rawType = item.vehicleType?.toString().trim().toUpperCase();
        const mappedVehicleType = vehicleTypeMap[rawType] || item.vehicleType;
        return {
          vehicleType: mappedVehicleType,
          activeCount: item.activeCount,
          idleCount: item.idleCount,
          offlineCount: item.offlineCount,
          total: totalCount
        };
      })
      .sort((a, b) => a.vehicleType.localeCompare(b.vehicleType));
    this.lastUpdatedEQuipmentTableData = [
      ...this.displayEquipmentInfoTableData
    ];
    let totalActiveCount = 0;
    let totalIdleCount = 0;
    let totalOfflineCount = 0;
    rawTableResponse?.forEach(element => {
      totalActiveCount += element?.activeCount;
      totalIdleCount += element?.idleCount;
      totalOfflineCount += element?.offlineCount;
    });

    this.totalCounts = { totalActiveCount, totalIdleCount, totalOfflineCount };
  }

  //to close equipment popup
  closeClicked(): void {
    this.moreEvent.emit(0);
    this.closeChange.emit(!this.close);
  }

  //to reset the equipment popup position
  resetElementPosition(element: HTMLElement): void {
    positionTerminalPopup(element, this.terminalUid, this.showFilter, this.clickPoint, this.bounds);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes && changes.equipmentResponse &&
      changes.equipmentResponse.currentValue !==
        changes.equipmentResponse.previousValue
    ) {
      this.getEquipmentTableInfo(this._terminalName());
      //data can arrive after the popup already opened/measured itself - reposition once it has rendered
      const element = document.getElementById(this.terminalUid);
      if (element) {
        setTimeout(() => this.resetElementPosition(element));
      }
    }
  }

  ngOnDestroy(): void {
    // Complete the destroy subject to clean up subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    // Reset all signals to their default state
    this._terminalName.set('');
    this._terminalAbbreviation.set('');
    this._top.set(0);
    this._left.set(30);
    this._close.set(false);
    this._showClose.set(false);
    this._collapseAll.set(false);
    //this._level.set(0);
    this.level = 0;
    this._showFilter.set(false);
    this._terminalUid.set(null);
    this._dynamicFontSize.set('');

    // Reset component properties
    this.zIndex = 1;

    // Reset app service states if needed
    if (this.showClose) {
      this.appService.iohTableZIndexLevels[this.level] = Math.max(
        0,
        (this.appService.iohTableZIndexLevels[this.level] || 0) - 1
      );
    }
  }
}
